import { Autocomplete, TextField } from '@mui/material';
import type { NewCustomer } from '@weft/types';
import { isNotEmptyString, type Nullable } from '@weft/utils';
import { countries } from 'countries-list';
import { get } from 'lodash';
import { useState, useMemo, useCallback } from 'react';
import React from 'react';
import { useForm, useFormContext } from 'react-hook-form';

type CountriesDropDownProps = {
  defaultSelectedCountry?: string,
  menuItemClickHandler?: (country: string) => void
};

export const CountriesDropDown: React.FC<CountriesDropDownProps> = ({ defaultSelectedCountry, menuItemClickHandler }) => {
  const [query, setQuery] = useState(defaultSelectedCountry ?? '');
  const { getFieldState, resetField, setValue } = useFormContext<NewCustomer>();
  const { formState: { errors } } = useForm<NewCustomer>();  

  const hasError = getFieldState('address.country')?.invalid;

  const filteredCountries = useMemo(() => {
    return Object.values(countries).filter((country) =>
      country.name.toLowerCase().includes(query.toLowerCase()),
    );
  }, [query]);

  const isAvailableCountry = useCallback((countryName: string) => {
    return Object.values(countries).some(({ name }) => name.toLowerCase() === countryName.toLowerCase());
  }, []);

  const handleCountrySelect = useCallback((event: React.SyntheticEvent<Element, Event>, countryName: Nullable<string>) => {
    const country = isNotEmptyString(countryName) ? Object.values(countries).find(({ name }) => name.toLowerCase() === countryName.toLowerCase())?.name || '' : '';  

    setQuery(countryName ?? '');
    menuItemClickHandler?.(country);

    isAvailableCountry(countryName ?? '')
      ? setValue('address.country', country)
      : resetField('address.country');
  }, [menuItemClickHandler, resetField, setValue, isAvailableCountry]);

  return (
    <Autocomplete
      value={query}
      onChange={handleCountrySelect}
      options={filteredCountries.map(country => country.name)}
      renderInput={(params) => (
        <TextField
          label="Country / region"
          {...params}
          error={hasError}
          placeholder="Country / region"
          helperText={isNotEmptyString(get(errors, 'address.country')?.message)}  
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
    />
  );
};